import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { NotificationBell } from '@/features/notifications';
import {
  WorkspaceListPage,
  workspaceListSearchSchema,
  type WorkspaceTab,
} from '@/features/workspaces';

export const Route = createFileRoute('/')({
  validateSearch: workspaceListSearchSchema,
  component: WorkspaceListRoute,
});

/**
 * Exported, like every other route component here: react-refresh can only
 * establish a fast-refresh boundary for components a module exports, and a
 * component defined but not exported beside `Route` fails that rule.
 */
export function WorkspaceListRoute() {
  const { create, tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  /*
    `replace`, so opening and closing a dialog does not stack history entries a
    reader has to walk back through — and so the Back button leaves the page, as
    it does everywhere else, rather than closing a dialog they may already have
    closed.

    `useCallback` because this is a prop on a component that renders the whole
    list; a new identity each render would defeat any memoisation below it.
  */
  const onCreateOpenChange = useCallback(
    (open: boolean) => {
      void navigate({
        search: (prev) => ({ ...prev, create: open ? true : undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  // The open tab (spec 200): replacing, like the dialog, so switching tabs does
  // not stack history entries between the reader and the page they came from.
  const onTabChange = useCallback(
    (next: WorkspaceTab) => {
      void navigate({
        search: (prev) => ({ ...prev, tab: next }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <WorkspaceListPage
      createOpen={create === true}
      onCreateOpenChange={onCreateOpenChange}
      tab={tab}
      onTabChange={onTabChange}
      // The bell, as every shell's bar has. Passed in from here because
      // `workspaces` can't import notifications without an import cycle (see
      // `WorkspaceListPageProps.headerEnd`).
      headerEnd={<NotificationBell />}
    />
  );
}
